import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { apiFetch } from "../services/api";
import { getEntityTypesList } from "../services/api";
import { entityAPI } from "../services/entityCache";
import { relationshipCache } from "../services/relationshipCache";
import { useEntityTypes } from "../hooks/useEntityTypes";
import * as d3 from "d3";
import { getTypeColors as getProfileColors } from "../utils/typeProfile";
import { normalizeEntityType } from "./relationships/SectionConfig";
import GraphHeader from "./GraphHeader";
import GraphCanvas from "./GraphCanvas";
import EntityTypeFilter from "./EntityTypeFilter";
import TipsPanel from "./TipsPanel";
import EntityDetailsPopup from "./EntityDetailsPopup";
import RelationshipDetailsPopup from "./RelationshipDetailsPopup";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

/** Match Entity Explorer: full relationship rows + related entity stubs (includes unapproved). */
const GRAPH_ENTITY_LOAD_OPTS = {
  includeRelationships: true,
  includeRelatedEntities: true,
  includeUnapproved: true,
};

/** Rejects placeholder / corrupted ids that become `/api/entities/[]` and churn fetches. */
function isValidGraphEntityId(id) {
  if (id == null) return false;
  const s = String(id).trim();
  if (!s) return false;
  if (s === "[]" || s === "null" || s === "undefined") return false;
  return true;
}

const GraphVisualization = ({
  initialNodes = [],
  initialEdges = [],
  centerEntityId = null,
  onClose = null,
  suppressSelection = false,
  title = null,
  autoLoadCenter = false,
  selectedEntityType = null,
  /** When true, render in normal document flow (e.g. Explorer detail) instead of a fullscreen overlay. */
  embedded = false,
  /** When embedded, stretch to fill the parent flex column (Explorer graph mode). */
  embeddedFillHeight = false,
  /** Explorer: back button + title row merged into GraphHeader (pass React node). */
  headerLeading = null,
  /** Explorer: no inner bordered frame — parent supplies the outer card. */
  embeddedUnifiedChrome = false,
}) => {
  const uiTone = embedded ? "light" : "dark";
  // Generate unique ID for this component instance
  const componentId = useRef(Math.random().toString(36).substr(2, 9)).current;

  // Debug logging for props
  console.log(`🔍 [${componentId}] GraphVisualization props:`, {
    initialNodesLength: initialNodes.length,
    initialEdgesLength: initialEdges.length,
    centerEntityId,
    suppressSelection,
    autoLoadCenter,
    title,
    sampleInitialNode: initialNodes[0],
    sampleInitialEdge: initialEdges[0],
  });

  // More detailed logging
  console.log(`🔍 [${componentId}] initialNodes array:`, initialNodes);
  console.log(`🔍 [${componentId}] initialEdges array:`, initialEdges);
  console.log(`🔍 [${componentId}] centerEntityId value:`, centerEntityId);

  const initialPropsRef = useRef({ initialNodes, initialEdges });
  initialPropsRef.current = { initialNodes, initialEdges };

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [centralNodeId, setCentralNodeId] = useState(null);
  const [allNodes, setAllNodes] = useState([]);
  const [allEdges, setAllEdges] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const layoutType = "circular";
  const [layoutKey, setLayoutKey] = useState(0);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [showStatusPanel, setShowStatusPanel] = useState(true);
  const [showNavPanel, setShowNavPanel] = useState(true);
  const [showEntityTypesPanel, setShowEntityTypesPanel] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [showNavTips, setShowNavTips] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTargetEntity, setMergeTargetEntity] = useState(null);
  const [merging, setMerging] = useState(false);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
  const [availableEntityTypes, setAvailableEntityTypes] = useState([]);
  const [isLoadingEntity, setIsLoadingEntity] = useState(false);

  const extractText = useCallback((value) => {
    if (value == null) return "";
    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value
        .map((item) => extractText(item))
        .join(" ")
        .trim();
    }
    if (React.isValidElement(value)) {
      return extractText(value.props?.children);
    }
    if (typeof value === "object") {
      if (value.label && value !== value.label) return extractText(value.label);
      if (value.name && value !== value.name) return extractText(value.name);
    }
    try {
      return String(value);
    } catch {
      return "";
    }
  }, []);

  // CRITICAL: Deep sanitization function to ensure NO React elements or objects in node data
  const sanitizeNodeDeeply = useCallback(
    (node) => {
      if (!node || typeof node !== "object") return null;

      // Ensure id is a string
      const id = String(node.id || "");
      if (!id) return null;

      // Deep sanitize name - must be a plain string
      let name = node.name;
      if (name != null) {
        name = extractText(name);
        if (typeof name !== "string") {
          console.error(
            "sanitizeNodeDeeply: name is not a string after extractText:",
            name,
            typeof name
          );
          name = "Unnamed";
        }
      } else {
        name = "Unnamed";
      }

      // Sanitize type
      const type = extractText(node.type || node.entity_type || "");

      // Sanitize description
      const description = extractText(node.description || "");

      // Build sanitized node - ONLY primitive values
      const sanitized = {
        id: String(id),
        name: String(name),
        type: String(type),
        entity_type: String(type),
        description: String(description),
        data: {
          label: String(name),
          entityType: String(type),
          description: String(description),
        },
      };

      // Validate that all values are primitives
      for (const key in sanitized) {
        const val = sanitized[key];
        if (val != null && typeof val === "object" && !Array.isArray(val)) {
          // Recursively sanitize nested objects
          const nested = {};
          for (const nestedKey in val) {
            nested[nestedKey] = String(extractText(val[nestedKey]));
          }
          sanitized[key] = nested;
        }
      }

      return sanitized;
    },
    [extractText]
  );

  // CRITICAL: Wrapper for setAllNodes that ensures all nodes are sanitized
  const setAllNodesSanitized = useCallback(
    (nodesOrUpdater) => {
      setAllNodes((prev) => {
        const newNodes =
          typeof nodesOrUpdater === "function"
            ? nodesOrUpdater(prev)
            : nodesOrUpdater;

        if (!Array.isArray(newNodes)) {
          console.error(
            "setAllNodesSanitized: newNodes is not an array:",
            newNodes
          );
          return prev;
        }

        // Deep sanitize every node
        const sanitized = newNodes
          .map((node) => sanitizeNodeDeeply(node))
          .filter(Boolean);

        // Final validation - ensure no React elements slipped through
        sanitized.forEach((node, index) => {
          if (
            React.isValidElement(node.name) ||
            React.isValidElement(node.data?.label)
          ) {
            console.error(
              `setAllNodesSanitized: React element found in node ${index}:`,
              node
            );
            node.name = "Unnamed";
            if (node.data) node.data.label = "Unnamed";
          }
        });

        return sanitized;
      });
    },
    [sanitizeNodeDeeply]
  );

  const resolveDisplayName = useCallback(
    (entity) => {
      const value =
        entity?.name ||
        entity?.display_name ||
        entity?.label ||
        entity?.title ||
        entity?.type ||
        "";
      const text = extractText(value);
      return text && text.trim().length > 0 ? text : "Unnamed";
    },
    [extractText]
  );

  const normalizeTypeValue = useCallback((type) => {
    return normalizeEntityType(type || "");
  }, []);

  const formatNodeForGraph = useCallback(
    (node) => {
      if (!node) return null;

      const rawType =
        node.entity_type ||
        node.type ||
        node.data?.entityType ||
        node.data?.type;
      const normalizedType = normalizeTypeValue(rawType);
      const labelSource = node.name || node.data?.label || "";
      const safeLabel = extractText(labelSource) || "";
      const label =
        normalizedType === "interface"
          ? safeLabel.replace("↔", "→")
          : safeLabel;
      const description = extractText(
        node.description || node.data?.description || ""
      );

      const approvalStatus =
        node.approval_status || node.approvalStatus || null;
      return {
        id: node.id,
        name: label,
        type: normalizedType,
        entity_type: normalizedType,
        description,
        data: {
          label,
          entityType: normalizedType,
          description,
          approvalStatus,
        },
      };
    },
    [normalizeTypeValue, extractText]
  );

  const { entityTypes, loading: entityTypesLoading } = useEntityTypes();

  // Consistent type colors via unified profile, with API fallback for unknown types
  const typeColors = useMemo(() => {
    const fixed = getProfileColors();
    const apiColors = {};
    entityTypes.forEach((type) => {
      const normalizedValue = normalizeTypeValue(type.value);
      if (!fixed[normalizedValue]) {
        apiColors[normalizedValue] = {
          bg: type.color || "#6b7280",
          border: type.color || "#9ca3af",
        };
      }
    });
    const fallback = { default: { bg: "#6b7280", border: "#9ca3af" } };
    return { ...fallback, ...apiColors, ...fixed };
  }, [entityTypes]);

  // Build label map for entity types to show human-friendly names in filters
  const typeLabels = useMemo(() => {
    const map = {};
    entityTypes.forEach((t) => {
      if (t?.value) {
        const normalizedValue = normalizeTypeValue(t.value);
        map[normalizedValue] = t.label || t.display_name || normalizedValue;
      }
    });
    return map;
  }, [entityTypes, normalizeTypeValue]);

  // Debounced search function
  const searchTimeoutRef = useRef(null);

  const performSearch = useCallback(async (searchValue) => {
    if (!searchValue.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      console.log("🔍 GraphVisualization: Searching entities from server...");
      const entities = await entityAPI.getEntitiesForAutocomplete(searchValue);

      // Sort entities to give interfaces last priority
      const sortedEntities = entities.sort((a, b) => {
        const aIsInterface = (a.entity_type || a.type) === "interface";
        const bIsInterface = (b.entity_type || b.type) === "interface";

        // If one is interface and other isn't, non-interface comes first
        if (aIsInterface && !bIsInterface) return 1;
        if (!aIsInterface && bIsInterface) return -1;

        // If both are interfaces or both are not interfaces, sort alphabetically by name
        return a.name.localeCompare(b.name);
      });

      console.log(
        "🔍 GraphVisualization: Found",
        sortedEntities.length,
        "entities from cache"
      );
      const sanitizedResults = sortedEntities
        .map((entity) => formatNodeForGraph(entity))
        .filter(Boolean);
      setSearchResults(sanitizedResults);
    } catch (error) {
      console.error("GraphVisualization: Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Event handlers
  const handleSearchChange = useCallback(
    async (e) => {
      const value = e.target.value;
      setSearchTerm(value);

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce search by 300ms
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(value);
      }, 300);
    },
    [performSearch]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Don't load all entity types from API - we'll derive them from the loaded graph nodes

  const handleEntitySelect = useCallback(
    async (entity, opts = {}) => {
      const quietRefresh = opts.quietRefresh === true;
      console.log(
        `🔍 [${componentId}] handleEntitySelect called with entity:`,
        entity
      );
      setCentralNodeId(entity.id);
      if (!quietRefresh) setIsLoadingEntity(true);

      // Clear search term and results when entity is selected
      setSearchTerm("");
      setSearchResults([]);

      try {
        // OPTIMIZED: Fetch entity data and relationships in parallel for better performance
        let fullEntity = entity;
        let allRelationships = [];

        // Fetch both entity data and relationships in parallel
        const [entityResult, relationshipsResult] = await Promise.allSettled([
          entityAPI.getEntityById(entity.id, GRAPH_ENTITY_LOAD_OPTS),
          relationshipCache.getRelationshipsForEntity(entity.id),
        ]);

        // Process entity result
        if (entityResult.status === "fulfilled" && entityResult.value) {
          fullEntity = entityResult.value;
          console.log(
            `🔍 Fetched full entity data with parent/children/successors/predecessors`
          );
        } else {
          console.log(
            `🔍 Using provided entity data (fetch failed or not needed)`
          );
        }

        // Process relationships result
        if (relationshipsResult.status === "fulfilled") {
          allRelationships = relationshipsResult.value || [];
          console.log(
            `🔍 Fetched ${allRelationships.length} relationships for entity`
          );
        } else {
          console.log(
            `🔍 Error fetching relationships by entity: ${relationshipsResult.reason}`
          );
        }

        // Prefer relationships embedded on GET /api/entities/:id (includes unapproved); by-entity cache route is approved-only.
        const embeddedRels = Array.isArray(fullEntity?.relationships)
          ? fullEntity.relationships
          : [];
        if (embeddedRels.length > 0) {
          allRelationships = embeddedRels;
          console.log(
            `🔍 Using ${allRelationships.length} relationships from entity payload (explorer parity)`
          );
        }

        if (
          (!allRelationships || allRelationships.length === 0) &&
          allEdges.length > 0
        ) {
          allRelationships = allEdges.map((edge) => ({
            id: edge.id,
            source_entity_id:
              edge.source?.id || edge.source_entity_id || edge.source,
            target_entity_id:
              edge.target?.id || edge.target_entity_id || edge.target,
            type: edge.type || edge.data?.relationshipType,
          }));
          console.log(
            `🔍 Using ${allRelationships.length} existing relationships as fallback`
          );
        }

        // Extract parent, children, successors, predecessors from full entity
        const parentId = fullEntity.parent;

        // Parse children - might be array, JSON string, null/undefined, or empty string
        let childrenIds = [];
        if (fullEntity.children) {
          if (Array.isArray(fullEntity.children)) {
            childrenIds = fullEntity.children;
          } else if (typeof fullEntity.children === "string") {
            const trimmed = fullEntity.children.trim();
            if (trimmed === "" || trimmed === "null" || trimmed === "[]") {
              childrenIds = [];
            } else {
              try {
                childrenIds = JSON.parse(trimmed);
                if (!Array.isArray(childrenIds)) childrenIds = [];
              } catch (e) {
                console.warn(`Failed to parse children JSON: ${e}`, trimmed);
                childrenIds = [];
              }
            }
          }
        }

        // Parse successors - might be array, JSON string, null/undefined, or empty string
        let successorsIds = [];
        if (fullEntity.successors) {
          if (Array.isArray(fullEntity.successors)) {
            successorsIds = fullEntity.successors;
          } else if (typeof fullEntity.successors === "string") {
            const trimmed = fullEntity.successors.trim();
            if (trimmed === "" || trimmed === "null" || trimmed === "[]") {
              successorsIds = [];
            } else {
              try {
                successorsIds = JSON.parse(trimmed);
                if (!Array.isArray(successorsIds)) successorsIds = [];
              } catch (e) {
                console.warn(`Failed to parse successors JSON: ${e}`, trimmed);
                successorsIds = [];
              }
            }
          }
        }

        // Parse predecessors - might be array, JSON string, null/undefined, or empty string
        let predecessorsIds = [];
        if (fullEntity.predecessors) {
          if (Array.isArray(fullEntity.predecessors)) {
            predecessorsIds = fullEntity.predecessors;
          } else if (typeof fullEntity.predecessors === "string") {
            const trimmed = fullEntity.predecessors.trim();
            if (trimmed === "" || trimmed === "null" || trimmed === "[]") {
              predecessorsIds = [];
            } else {
              try {
                predecessorsIds = JSON.parse(trimmed);
                if (!Array.isArray(predecessorsIds)) predecessorsIds = [];
              } catch (e) {
                console.warn(
                  `Failed to parse predecessors JSON: ${e}`,
                  trimmed
                );
                predecessorsIds = [];
              }
            }
          }
        }

        console.log(`🔍 Entity hierarchy data for ${entity.id}:`, {
          parentId,
          childrenCount: childrenIds.length,
          childrenIds,
          childrenRaw: fullEntity.children,
          childrenRawType: typeof fullEntity.children,
          successorsCount: successorsIds.length,
          successorsIds,
          successorsRaw: fullEntity.successors,
          successorsRawType: typeof fullEntity.successors,
          predecessorsCount: predecessorsIds.length,
          predecessorsIds,
          predecessorsRaw: fullEntity.predecessors,
          predecessorsRawType: typeof fullEntity.predecessors,
        });
        console.log(`🔍 Full entity object keys:`, Object.keys(fullEntity));
        console.log(`🔍 Full entity children field:`, fullEntity.children);
        console.log(`🔍 Full entity parent field:`, fullEntity.parent);

        // Reverse lookup: Find children entities where parent = this entity's ID
        // This is needed because children might not be stored in the children array
        // OPTIMIZED: Skip reverse lookup if we already have children from entity data
        // Only do reverse lookup if childrenIds is empty to avoid slow getEntities() call
        if (childrenIds.length === 0) {
          try {
            // Try to use a more efficient endpoint if available, otherwise skip
            // The reverse lookup is optional and can be slow with many entities
            console.log(
              `🔍 Skipping reverse lookup for children to improve performance`
            );
            // Note: If children are stored in the parent field, they should be in the children array
            // If not, they can be loaded when the user expands connections
          } catch (error) {
            console.warn(
              `🔍 Error doing reverse lookup for children: ${error}`
            );
          }
        }

        // Create edges for parent/child/successor/predecessor connections
        const hierarchyEdges = [];
        if (parentId && isValidGraphEntityId(parentId)) {
          hierarchyEdges.push({
            id: `parent_${entity.id}_${parentId}`,
            source_entity_id: parentId,
            target_entity_id: entity.id,
            type: "Parent",
            data: { relationshipType: "Parent" },
          });
        }
        childrenIds.forEach((childId) => {
          if (!isValidGraphEntityId(childId)) return;
          hierarchyEdges.push({
            id: `child_${entity.id}_${childId}`,
            source_entity_id: entity.id,
            target_entity_id: childId,
            type: "Child",
            data: { relationshipType: "Child" },
          });
        });
        successorsIds.forEach((successorId) => {
          if (!isValidGraphEntityId(successorId)) return;
          hierarchyEdges.push({
            id: `successor_${entity.id}_${successorId}`,
            source_entity_id: entity.id,
            target_entity_id: successorId,
            type: "Successor",
            data: { relationshipType: "Successor" },
          });
        });
        predecessorsIds.forEach((predecessorId) => {
          if (!isValidGraphEntityId(predecessorId)) return;
          hierarchyEdges.push({
            id: `predecessor_${entity.id}_${predecessorId}`,
            source_entity_id: predecessorId,
            target_entity_id: entity.id,
            type: "Predecessor",
            data: { relationshipType: "Predecessor" },
          });
        });

        // Merge hierarchy edges with regular relationships
        const allEdgesWithHierarchy = [...allRelationships, ...hierarchyEdges];

        console.log(
          `🔍 Relationship counts: allRelationships=${allRelationships.length}, hierarchyEdges=${hierarchyEdges.length}, total=${allEdgesWithHierarchy.length}`
        );

        // Filter relationships for this entity
        const entityRelationships = allEdgesWithHierarchy.filter(
          (rel) =>
            rel.source_entity_id === entity.id ||
            rel.target_entity_id === entity.id
        );

        console.log(
          `🔍 Filtered to ${entityRelationships.length} relationships for entity ${entity.id}`
        );

        // Get IDs of connected entities (from relationships and hierarchy)
        const connectedEntityIds = new Set();
        entityRelationships.forEach((rel) => {
          if (rel.source_entity_id !== entity.id)
            connectedEntityIds.add(rel.source_entity_id);
          if (rel.target_entity_id !== entity.id)
            connectedEntityIds.add(rel.target_entity_id);
        });

        // Add parent, children, successors, predecessors to connected IDs
        if (parentId && isValidGraphEntityId(parentId))
          connectedEntityIds.add(parentId);
        childrenIds.forEach((id) => {
          if (isValidGraphEntityId(id)) connectedEntityIds.add(id);
        });
        successorsIds.forEach((id) => {
          if (isValidGraphEntityId(id)) connectedEntityIds.add(id);
        });
        predecessorsIds.forEach((id) => {
          if (isValidGraphEntityId(id)) connectedEntityIds.add(id);
        });

        // Get connected entities - prefer existing data, then fetch missing
        let connectedEntities = [];
        const entityIds = Array.from(connectedEntityIds).filter(
          isValidGraphEntityId
        );
        console.log(
          `🔍 Connected entity IDs to fetch: ${entityIds.length} total`,
          entityIds
        );
        if (allNodes.length > 0) {
          connectedEntities = allNodes.filter((node) =>
            connectedEntityIds.has(node.id)
          );
          console.log(
            `🔍 Found ${connectedEntities.length} connected entities from existing data`
          );
        }
        console.log(
          `🔍 Entity fetch check: connectedEntities.length=${connectedEntities.length}, entityIds.length=${entityIds.length}`
        );
        if (connectedEntities.length < entityIds.length) {
          console.log(
            `🔍 Fetching entities to resolve missing connected nodes (need ${entityIds.length}, have ${connectedEntities.length})`
          );
          const existingIds = new Set(connectedEntities.map((e) => e.id));
          const missingIds = entityIds.filter((id) => !existingIds.has(id));
          console.log(`🔍 Missing entity IDs to fetch:`, missingIds);

          // Use bulk fetch endpoint for efficiency
          try {
            const fetched = await entityAPI.getEntitiesByIds(missingIds);
            connectedEntities = [...connectedEntities, ...fetched];
            console.log(
              `🔍 Fetched ${fetched.length} entities from bulk endpoint (requested ${missingIds.length})`
            );
          } catch (error) {
            console.error(`🔍 Bulk fetch failed: ${error}`);
            // OPTIMIZED: Skip slow getEntities() fallback - fetch missing entities individually instead
            if (missingIds.length > 0 && missingIds.length <= 20) {
              try {
                console.log(
                  `🔍 Fetching ${missingIds.length} missing entities individually as fallback`
                );
                const individualFetches = await Promise.allSettled(
                  missingIds.map((id) => entityAPI.getEntityById(id))
                );
                const fetched = individualFetches
                  .filter(
                    (result) => result.status === "fulfilled" && result.value
                  )
                  .map((result) => result.value);
                connectedEntities = [...connectedEntities, ...fetched];
                console.log(
                  `🔍 Fetched ${fetched.length} entities individually as fallback`
                );
              } catch (fallbackError) {
                console.error(
                  `🔍 Individual fetch fallback also failed: ${fallbackError}`
                );
              }
            } else {
              console.warn(
                `🔍 Too many missing entities (${missingIds.length}), skipping fallback fetch`
              );
            }
          }

          console.log(
            `🔍 Resolved ${connectedEntities.length} connected entities total (need ${entityIds.length})`
          );
        }

        // Format nodes and edges for D3
        const nodes = [fullEntity, ...connectedEntities]
          .map((node) => formatNodeForGraph(node))
          .filter(Boolean);

        const edges = entityRelationships.map((rel) => ({
          id: rel.id,
          source: rel.source_entity_id,
          target: rel.target_entity_id,
          type: rel.type || "Unknown",
          data: {
            relationshipType: rel.type || "Unknown",
            relationshipStatus: rel.status || "Approved",
          },
        }));

        console.log(
          `🔍 Graph state: ${nodes.length} nodes, ${edges.length} edges ready for display`
        );

        setAllNodesSanitized(nodes);
        setAllEdges(edges);

        // Don't override entity types if they were loaded from API
        // Only set from data if no types are available yet
        if (availableEntityTypes.length === 0) {
          // Entity types will be set from allNodes in the useEffect hook
        }

        // Force graph redraw by updating layout key
        setLayoutKey((prev) => prev + 1);

        // Fit to view after graph data is set - use delay to ensure graph has rendered
        if (handleFitToView.current) {
          setTimeout(
            () => handleFitToView.current && handleFitToView.current(),
            300
          );
        }

        setIsInitialized(true);
        console.log(
          `🔍 [${componentId}] handleEntitySelect completed successfully`
        );
      } catch (error) {
        console.error("Error loading entity:", error);
        setError("Failed to load entity relationships");
      } finally {
        setIsLoadingEntity(false);
        console.log(
          `🔍 [${componentId}] handleEntitySelect finished (loading: false)`
        );
      }
    },
    [allEdges, allNodes, entityAPI, relationshipCache]
  );

  const handleEntitySelectRef = useRef(handleEntitySelect);
  handleEntitySelectRef.current = handleEntitySelect;

  const handleNodeClick = useCallback((event, d) => {
    setSelectedEntity(d);
    setSelectedEdge(null);
  }, []);

  const handleEdgeClick = useCallback((event, d) => {
    setSelectedEdge(d);
    setSelectedEntity(null);
  }, []);

  const handleNodeHover = useCallback((event, d) => {
    // Show node label
    if (typeof d3 !== "undefined") {
      d3.selectAll(".node-label").style("opacity", (labelData) =>
        labelData.id === d.id ? 1 : 0
      );
    }
  }, []);

  const handleEdgeHover = useCallback((event, d) => {
    // Show edge label
    if (typeof d3 !== "undefined") {
      d3.selectAll(".edge-label").style("opacity", (labelData) =>
        labelData.id === d.id ? 1 : 0
      );
    }
  }, []);

  const handleResetLayout = useCallback(() => {
    setLayoutKey((prev) => prev + 1);
    // Also call the ref function if available
    if (handleResetLayoutRef.current) {
      handleResetLayoutRef.current();
    }
  }, []);

  const handleFilterToggle = useCallback(() => {
    setShowEntityTypesPanel((prev) => !prev);
  }, []);

  const handleTipsToggle = useCallback(() => {
    setShowNavTips((prev) => !prev);
  }, []);

  const handleLoadConnections = useCallback(
    async (entity) => {
      const targetEntity = entity || selectedEntity;
      if (!targetEntity) return;
      setIsLoadingEntity(true);
      try {
        // OPTIMIZED: Fetch entity data and relationships in parallel for better performance
        let fullEntity = targetEntity;
        let fetchedRelationships = [];

        // Fetch both entity data and relationships in parallel
        const [entityResult, relationshipsResult] = await Promise.allSettled([
          entityAPI.getEntityById(targetEntity.id, GRAPH_ENTITY_LOAD_OPTS),
          relationshipCache.getRelationshipsForEntity(targetEntity.id),
        ]);

        // Process entity result
        if (entityResult.status === "fulfilled" && entityResult.value) {
          fullEntity = entityResult.value;
          console.log(
            `🔍 Load connections: fetched full entity data with parent/children/successors/predecessors`
          );
        } else {
          console.log(
            `🔍 Load connections: Using provided entity data (fetch failed or not needed)`
          );
        }

        // Process relationships result
        if (relationshipsResult.status === "fulfilled") {
          fetchedRelationships = relationshipsResult.value || [];
          console.log(
            `🔍 Load connections: fetched ${fetchedRelationships.length} relationships for`,
            targetEntity.id
          );
        } else {
          console.log(
            `🔍 Load connections: Error fetching relationships: ${relationshipsResult.reason}`
          );
        }

        const embeddedLoad = Array.isArray(fullEntity?.relationships)
          ? fullEntity.relationships
          : [];
        if (embeddedLoad.length > 0) {
          fetchedRelationships = embeddedLoad;
        }

        // Extract parent, children, successors, predecessors from full entity
        const parentId = fullEntity.parent;

        // Parse children - might be array, JSON string, or null/undefined
        let childrenIds = [];
        if (fullEntity.children) {
          if (Array.isArray(fullEntity.children)) {
            childrenIds = fullEntity.children;
          } else if (typeof fullEntity.children === "string") {
            const trimmed = fullEntity.children.trim();
            if (trimmed === "" || trimmed === "null" || trimmed === "[]") {
              childrenIds = [];
            } else {
              try {
                childrenIds = JSON.parse(trimmed);
                if (!Array.isArray(childrenIds)) childrenIds = [];
              } catch (e) {
                console.warn(
                  `Load connections: Failed to parse children JSON: ${e}`,
                  trimmed
                );
                childrenIds = [];
              }
            }
          }
        }

        // Reverse lookup: Find children entities where parent = this entity's ID
        // OPTIMIZED: Skip reverse lookup if we already have children from entity data
        // Only do reverse lookup if childrenIds is empty to avoid slow getEntities() call
        if (childrenIds.length === 0) {
          try {
            // Try to use a more efficient endpoint if available, otherwise skip
            // The reverse lookup is optional and can be slow with many entities
            console.log(
              `🔍 Load connections: Skipping reverse lookup for children to improve performance`
            );
            // Note: If children are stored in the parent field, they should be in the children array
            // If not, they can be loaded when the user expands connections
          } catch (error) {
            console.warn(
              `🔍 Load connections: Error doing reverse lookup for children: ${error}`
            );
          }
        }

        // Parse successors - might be array, JSON string, or null/undefined
        let successorsIds = [];
        if (fullEntity.successors) {
          if (Array.isArray(fullEntity.successors)) {
            successorsIds = fullEntity.successors;
          } else if (typeof fullEntity.successors === "string") {
            try {
              successorsIds = JSON.parse(fullEntity.successors);
              if (!Array.isArray(successorsIds)) successorsIds = [];
            } catch (e) {
              console.warn(
                `Load connections: Failed to parse successors JSON: ${e}`
              );
              successorsIds = [];
            }
          }
        }

        // Parse predecessors - might be array, JSON string, or null/undefined
        let predecessorsIds = [];
        if (fullEntity.predecessors) {
          if (Array.isArray(fullEntity.predecessors)) {
            predecessorsIds = fullEntity.predecessors;
          } else if (typeof fullEntity.predecessors === "string") {
            try {
              predecessorsIds = JSON.parse(fullEntity.predecessors);
              if (!Array.isArray(predecessorsIds)) predecessorsIds = [];
            } catch (e) {
              console.warn(
                `Load connections: Failed to parse predecessors JSON: ${e}`
              );
              predecessorsIds = [];
            }
          }
        }

        // Create edges for parent/child/successor/predecessor connections
        const hierarchyEdges = [];
        if (parentId) {
          hierarchyEdges.push({
            id: `parent_${targetEntity.id}_${parentId}`,
            source_entity_id: parentId,
            target_entity_id: targetEntity.id,
            type: "Parent",
            data: { relationshipType: "Parent" },
          });
        }
        childrenIds.forEach((childId) => {
          hierarchyEdges.push({
            id: `child_${targetEntity.id}_${childId}`,
            source_entity_id: targetEntity.id,
            target_entity_id: childId,
            type: "Child",
            data: { relationshipType: "Child" },
          });
        });
        successorsIds.forEach((successorId) => {
          hierarchyEdges.push({
            id: `successor_${targetEntity.id}_${successorId}`,
            source_entity_id: targetEntity.id,
            target_entity_id: successorId,
            type: "Successor",
            data: { relationshipType: "Successor" },
          });
        });
        predecessorsIds.forEach((predecessorId) => {
          hierarchyEdges.push({
            id: `predecessor_${targetEntity.id}_${predecessorId}`,
            source_entity_id: predecessorId,
            target_entity_id: targetEntity.id,
            type: "Predecessor",
            data: { relationshipType: "Predecessor" },
          });
        });

        // Merge hierarchy edges with regular relationships
        const allRelationships = [...fetchedRelationships, ...hierarchyEdges];

        // Build set of connected IDs (excluding the selected node)
        const connectedIds = new Set();
        allRelationships.forEach((rel) => {
          if (rel.source_entity_id !== targetEntity.id)
            connectedIds.add(rel.source_entity_id);
          if (rel.target_entity_id !== targetEntity.id)
            connectedIds.add(rel.target_entity_id);
        });

        // Add parent, children, successors, predecessors to connected IDs
        if (parentId) connectedIds.add(parentId);
        childrenIds.forEach((id) => connectedIds.add(id));
        successorsIds.forEach((id) => connectedIds.add(id));
        predecessorsIds.forEach((id) => connectedIds.add(id));

        // Ensure target node exists in graph
        const existingNodeIds = new Set(allNodes.map((n) => n.id));
        if (!existingNodeIds.has(targetEntity.id)) {
          const baseNode = formatNodeForGraph(targetEntity);
          if (baseNode) {
            setAllNodesSanitized((prev) => [...prev, baseNode]);
          }
          existingNodeIds.add(targetEntity.id);
        }

        // Resolve connected entities from existing nodes, fetch missing
        let connectedEntities = allNodes.filter((n) => connectedIds.has(n.id));
        if (connectedEntities.length < connectedIds.size) {
          const existingIds = new Set(connectedEntities.map((e) => e.id));
          const missingIds = Array.from(connectedIds).filter(
            (id) => !existingIds.has(id)
          );

          // Try bulk fetch first using the efficient endpoint
          try {
            const fetched = await entityAPI.getEntitiesByIds(missingIds);
            connectedEntities = [...connectedEntities, ...fetched];
            console.log(
              `🔍 Load connections: Fetched ${fetched.length} entities from bulk endpoint (requested ${missingIds.length})`
            );
          } catch (e) {
            console.warn(
              "Load connections: Bulk fetch failed, trying individual fetches",
              e
            );
          }

          // Fetch remaining missing entities individually
          const currentIds = new Set(connectedEntities.map((e) => e.id));
          const stillMissing = missingIds.filter((id) => !currentIds.has(id));

          if (stillMissing.length > 0) {
            console.log(
              `🔍 Load connections: Fetching ${stillMissing.length} missing entities individually`
            );
            const individualFetches = await Promise.allSettled(
              stillMissing.map((id) => entityAPI.getEntityById(id))
            );
            const fetched = individualFetches
              .filter((result) => result.status === "fulfilled" && result.value)
              .map((result) => result.value);
            connectedEntities = [...connectedEntities, ...fetched];
            console.log(
              `🔍 Load connections: Fetched ${fetched.length} entities individually`
            );
          }
        }

        // Format nodes for graph and add any that are new
        // CRITICAL: Use formatNodeForGraph to ensure all names are sanitized to plain strings
        const formattedNewNodes = connectedEntities
          .filter((e) => !existingNodeIds.has(e.id))
          .map((e) => formatNodeForGraph(e))
          .filter(Boolean);

        if (formattedNewNodes.length > 0) {
          setAllNodesSanitized((prev) => {
            const existing = new Set(prev.map((n) => n.id));
            const toAdd = formattedNewNodes.filter((n) => !existing.has(n.id));
            const merged = [...prev, ...toAdd];
            return merged;
          });
          formattedNewNodes.forEach((n) => existingNodeIds.add(n.id));
        }

        // Merge edges (including hierarchy edges)
        const existingEdgeKeys = new Set(
          allEdges.map(
            (e) =>
              `${e.id}|${e.source_entity_id || e.source}|${e.target_entity_id || e.target}|${e.type}`
          )
        );
        const candidateEdges = allRelationships.map((rel) => ({
          id: rel.id,
          source: rel.source_entity_id,
          target: rel.target_entity_id,
          type: rel.type || "Unknown",
          data: {
            relationshipType: rel.type || "Unknown",
            relationshipStatus: rel.status || "Approved",
          },
        }));
        const newEdges = candidateEdges.filter(
          (e) =>
            !existingEdgeKeys.has(`${e.id}|${e.source}|${e.target}|${e.type}`)
        );

        if (newEdges.length > 0) {
          setAllEdges((prev) => {
            const merged = [...prev, ...newEdges];
            return merged;
          });
        }

        // Force graph redraw
        setLayoutKey((prev) => prev + 1);
        // Fit to view to reveal newly added connections - use longer delay to ensure graph has rendered
        if (handleFitToView.current) {
          setTimeout(
            () => handleFitToView.current && handleFitToView.current(),
            300
          );
        }
      } catch (error) {
        console.error("Error loading connections:", error);
      } finally {
        setIsLoadingEntity(false);
      }
    },
    [selectedEntity, allNodes, allEdges]
  );

  const handleZoomIn = useRef(null);
  const handleZoomOut = useRef(null);
  const handleFitToView = useRef(null);
  const handleResetLayoutRef = useRef(null);

  // Memoize processed data with stable dependencies
  const processedData = useMemo(() => {
    console.log(`🔍 [${componentId}] Processing data:`, {
      initialNodesLength: initialNodes.length,
      initialEdgesLength: initialEdges.length,
      centerEntityId,
      allNodesLength: allNodes.length,
      allEdgesLength: allEdges.length,
      centralNodeId,
    });

    // Use internal state if available, otherwise use props
    const nodesToProcess = allNodes.length > 0 ? allNodes : initialNodes;
    const edgesToProcess = allEdges.length > 0 ? allEdges : initialEdges;
    const centerId = centralNodeId || centerEntityId;

    if (!nodesToProcess || nodesToProcess.length === 0) {
      console.log(`🔍 [${componentId}] No initial nodes, returning empty data`);
      return { processedNodes: [], processedEdges: [], types: [] };
    }

    // Process nodes to ensure proper structure
    const processedNodes = nodesToProcess.map((node) => {
      const rawType = node.type || node.data?.entityType || node.entity_type;
      const normalizedType = normalizeTypeValue(rawType);
      const labelSource = node.name || node.data?.label || "";
      const label =
        normalizedType === "interface"
          ? labelSource.replace("↔", "→")
          : labelSource;

      return {
        id: node.id,
        name: label,
        type: normalizedType,
        description: node.description || node.data?.description,
        data: {
          label,
          entityType: normalizedType,
          description: node.description || node.data?.description,
        },
      };
    });

    // Process edges to ensure proper structure
    const processedEdges = (edgesToProcess || []).map((edge) => ({
      id: edge.id,
      source: edge.source || edge.source_entity_id,
      target: edge.target || edge.target_entity_id,
      type: edge.type || edge.data?.relationshipType || "Unknown",
      data: {
        relationshipType: edge.type || edge.data?.relationshipType || "Unknown",
      },
    }));

    // Get available entity types
    const types = [
      ...new Set(processedNodes.map((node) => node.type).filter(Boolean)),
    ];

    return { processedNodes, processedEdges, types };
  }, [
    centerEntityId,
    initialNodes,
    initialEdges,
    allNodes,
    allEdges,
    centralNodeId,
  ]);

  /** Avoid re-seeding from Explorer bootstrap when selectedEntityFull expands (same center). */
  const propsSeedAppliedForCenterRef = useRef(null);

  // Initialize state when initialNodes/initialEdges change
  useEffect(() => {
    console.log(`🔍 [${componentId}] useEffect triggered:`, {
      initialNodesLength: initialNodes.length,
      initialEdgesLength: initialEdges.length,
      centerEntityId,
      isInitialized,
    });

    if (!centerEntityId || initialNodes.length === 0) {
      console.log(
        `🔍 [${componentId}] No initial nodes or center id, skipping initialization`
      );
      return;
    }

    // One props-driven seed per center — API refresh expands graph without wiping layout twice.
    if (propsSeedAppliedForCenterRef.current === centerEntityId) {
      console.log(
        `🔍 [${componentId}] Props seed already applied for center, skipping`
      );
      return;
    }
    propsSeedAppliedForCenterRef.current = centerEntityId;

    console.log(
      `🔍 [${componentId}] GraphVisualization initialization with data:`,
      {
        centerEntityId,
        initialNodesLength: initialNodes.length,
        initialEdgesLength: initialEdges.length,
      }
    );

    // Process parent/child/successor/predecessor connections from initialNodes
    const hierarchyEdges = [];
    const connectedEntityIds = new Set();

    initialNodes.forEach((node) => {
      const parentId = node.parent;
      const childrenIds = Array.isArray(node.children) ? node.children : [];
      const successorsIds = Array.isArray(node.successors)
        ? node.successors
        : [];
      const predecessorsIds = Array.isArray(node.predecessors)
        ? node.predecessors
        : [];

      if (parentId && isValidGraphEntityId(parentId)) {
        hierarchyEdges.push({
          id: `parent_${node.id}_${parentId}`,
          source_entity_id: parentId,
          target_entity_id: node.id,
          type: "Parent",
          data: { relationshipType: "Parent" },
        });
        connectedEntityIds.add(parentId);
      }
      childrenIds.forEach((childId) => {
        if (!isValidGraphEntityId(childId)) return;
        hierarchyEdges.push({
          id: `child_${node.id}_${childId}`,
          source_entity_id: node.id,
          target_entity_id: childId,
          type: "Child",
          data: { relationshipType: "Child" },
        });
        connectedEntityIds.add(childId);
      });
      successorsIds.forEach((successorId) => {
        if (!isValidGraphEntityId(successorId)) return;
        hierarchyEdges.push({
          id: `successor_${node.id}_${successorId}`,
          source_entity_id: node.id,
          target_entity_id: successorId,
          type: "Successor",
          data: { relationshipType: "Successor" },
        });
        connectedEntityIds.add(successorId);
      });
      predecessorsIds.forEach((predecessorId) => {
        if (!isValidGraphEntityId(predecessorId)) return;
        hierarchyEdges.push({
          id: `predecessor_${node.id}_${predecessorId}`,
          source_entity_id: predecessorId,
          target_entity_id: node.id,
          type: "Predecessor",
          data: { relationshipType: "Predecessor" },
        });
        connectedEntityIds.add(predecessorId);
      });
    });

    // Merge hierarchy edges with initial edges
    const normalizedInitialNodes = initialNodes
      .map((node) => formatNodeForGraph(node))
      .filter(Boolean);
    const allInitialEdges = [...initialEdges, ...hierarchyEdges];

    setCentralNodeId(centerEntityId);
    setAllNodesSanitized(normalizedInitialNodes);
    setAllEdges(allInitialEdges);

    // Entity types will be set from allNodes in the useEffect hook

    setLayoutKey((prev) => prev + 1);
    setIsInitialized(true);

    // Fit to view after initial data is loaded
    if (handleFitToView.current) {
      setTimeout(
        () => handleFitToView.current && handleFitToView.current(),
        300
      );
    }

    console.log(
      `🔍 [${componentId}] GraphVisualization initialized successfully`
    );
  }, [initialNodes, initialEdges, centerEntityId]); // Use individual props instead of processedData

  // Track entities we're currently fetching to avoid duplicate requests
  const fetchingEntityIdsRef = useRef(new Set());

  // Fetch missing entities referenced in edges to resolve "Unknown" types
  useEffect(() => {
    const fetchMissingEntities = async () => {
      if (allEdges.length === 0) return;

      // Collect all entity IDs referenced in edges
      const referencedIds = new Set();
      allEdges.forEach((edge) => {
        const sourceId = edge.source?.id || edge.source_entity_id || edge.source;
        const targetId = edge.target?.id || edge.target_entity_id || edge.target;
        if (sourceId && isValidGraphEntityId(sourceId))
          referencedIds.add(sourceId);
        if (targetId && isValidGraphEntityId(targetId))
          referencedIds.add(targetId);
      });

      // Find missing entity IDs (not in allNodes and not currently being fetched)
      const existingIds = new Set(allNodes.map((n) => n.id));
      const missingIds = Array.from(referencedIds).filter(
        (id) => !existingIds.has(id) && !fetchingEntityIdsRef.current.has(id)
      );

      if (missingIds.length === 0) return;

      // Mark these IDs as being fetched
      missingIds.forEach((id) => fetchingEntityIdsRef.current.add(id));

      console.log(
        `🔍 Found ${missingIds.length} missing entities in edges, fetching...`
      );

      try {
        // Fetch missing entities in bulk
        const fetched = await entityAPI.getEntitiesByIds(missingIds);
        if (fetched && fetched.length > 0) {
          console.log(
            `🔍 Fetched ${fetched.length} missing entities, updating nodes`
          );
          const formattedNodes = fetched
            .map((node) => formatNodeForGraph(node))
            .filter(Boolean);
          setAllNodesSanitized((prev) => {
            const existingIds = new Set(prev.map((n) => n.id));
            const newNodes = formattedNodes.filter(
              (n) => !existingIds.has(n.id)
            );
            // Remove fetched IDs from the fetching set
            formattedNodes.forEach((n) =>
              fetchingEntityIdsRef.current.delete(n.id)
            );
            return [...prev, ...newNodes];
          });
        } else {
          // Remove IDs that weren't found from the fetching set
          missingIds.forEach((id) => fetchingEntityIdsRef.current.delete(id));
        }
      } catch (error) {
        console.error("Error fetching missing entities:", error);
        // Remove failed IDs from the fetching set
        missingIds.forEach((id) => fetchingEntityIdsRef.current.delete(id));
        // Fallback: try individual fetches for a small number of missing entities
        if (missingIds.length <= 10) {
          try {
            // Re-mark IDs as being fetched for fallback
            missingIds.forEach((id) => fetchingEntityIdsRef.current.add(id));
            const individualFetches = await Promise.allSettled(
              missingIds.map((id) => entityAPI.getEntityById(id))
            );
            const fetched = individualFetches
              .filter(
                (result) => result.status === "fulfilled" && result.value
              )
              .map((result) => result.value);
            if (fetched.length > 0) {
              const formattedNodes = fetched
                .map((node) => formatNodeForGraph(node))
                .filter(Boolean);
              setAllNodesSanitized((prev) => {
                const existingIds = new Set(prev.map((n) => n.id));
                const newNodes = formattedNodes.filter(
                  (n) => !existingIds.has(n.id)
                );
                // Remove fetched IDs from the fetching set
                formattedNodes.forEach((n) =>
                  fetchingEntityIdsRef.current.delete(n.id)
                );
                return [...prev, ...newNodes];
              });
            } else {
              // Remove IDs that weren't found from the fetching set
              missingIds.forEach((id) =>
                fetchingEntityIdsRef.current.delete(id)
              );
            }
          } catch (fallbackError) {
            console.error("Fallback fetch also failed:", fallbackError);
            // Remove failed IDs from the fetching set
            missingIds.forEach((id) =>
              fetchingEntityIdsRef.current.delete(id)
            );
          }
        }
      }
    };

    // Debounce to avoid too many fetches
    const timeoutId = setTimeout(fetchMissingEntities, 500);
    return () => clearTimeout(timeoutId);
  }, [allEdges, allNodes, entityAPI, formatNodeForGraph]);

  /** Load / refresh center entity from API (quiet when Explorer passed in-memory bootstrap). */
  useEffect(() => {
    if (!(suppressSelection || autoLoadCenter) || !centerEntityId) return;
    const hasBootstrap =
      (initialPropsRef.current.initialNodes?.length || 0) > 0 ||
      (initialPropsRef.current.initialEdges?.length || 0) > 0;
    let cancelled = false;
    void (async () => {
      try {
        const entity = await entityAPI.getEntityById(
          centerEntityId,
          GRAPH_ENTITY_LOAD_OPTS
        );
        if (cancelled) return;
        if (!entity) {
          if (!hasBootstrap) {
            setIsLoadingEntity(false);
            setError("Entity not found.");
          }
          return;
        }
        setError(null);
        await handleEntitySelectRef.current(entity, {
          quietRefresh: hasBootstrap,
        });
      } catch (e) {
        console.error("GraphVisualization: center entity load failed", e);
        if (!hasBootstrap) {
          setIsLoadingEntity(false);
          setError(e?.message || "Could not load graph.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [suppressSelection, autoLoadCenter, centerEntityId]);

  // (removed autoLoadCenter-based force loading to revert to previous behavior)

  // Auto-fit to view when nodes or edges change (after graph has rendered)
  useEffect(() => {
    if (isInitialized && allNodes.length > 0 && handleFitToView.current) {
      // Use a delay to ensure the graph component has rendered the new data
      const timeoutId = setTimeout(() => {
        if (handleFitToView.current) {
          handleFitToView.current();
        }
      }, 400); // Slightly longer delay to ensure D3 has finished rendering

      return () => clearTimeout(timeoutId);
    }
  }, [allNodes.length, allEdges.length, isInitialized, layoutKey]);

  // Auto-fit to view when tab/window becomes visible (handles tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When tab becomes visible and graph is initialized, fit to view
      if (
        !document.hidden &&
        isInitialized &&
        allNodes.length > 0 &&
        handleFitToView.current
      ) {
        // Use a delay to ensure the graph container is properly sized
        setTimeout(() => {
          if (handleFitToView.current && !document.hidden) {
            handleFitToView.current();
          }
        }, 200);
      }
    };

    // Listen for visibility changes (tab switching)
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Also listen for window focus (in case of window switching)
    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, [isInitialized, allNodes.length]);

  // Auto-fit to view on window resize (ensures graph stays visible after resize)
  useEffect(() => {
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (isInitialized && allNodes.length > 0 && handleFitToView.current) {
          handleFitToView.current();
        }
      }, 300);
    };

    window.addEventListener("resize", debouncedResize);

    return () => {
      window.removeEventListener("resize", debouncedResize);
      clearTimeout(resizeTimeout);
    };
  }, [isInitialized, allNodes.length]);

  // Filtered data for graph
  // Ensure all types are selected by default when list becomes available
  useEffect(() => {
    if (availableEntityTypes.length > 0 && selectedEntityTypes.length === 0) {
      setSelectedEntityTypes(availableEntityTypes);
    }
  }, [availableEntityTypes]);

  // Handle selectedEntityType prop (for entity type citations)
  useEffect(() => {
    if (selectedEntityType) {
      // Normalize the entity type to match the format used in the graph
      const normalizeType = (type) => {
        // Try to match against available types first
        const normalized = availableEntityTypes.find(
          (t) => t.toLowerCase() === type.toLowerCase()
        );
        if (normalized) return normalized;

        // Fallback: return the type as-is (will be normalized by the filter)
        return type;
      };

      const normalizedType = normalizeType(selectedEntityType);
      console.log(
        `📊 [GraphVisualization] Setting selected entity type filter: ${normalizedType}`
      );
      setSelectedEntityTypes([normalizedType]);
    }
  }, [selectedEntityType, availableEntityTypes]);

  // Derive available entity types ONLY from nodes in the loaded graph
  useEffect(() => {
    if (allNodes.length === 0) {
      setAvailableEntityTypes([]);
      return;
    }

    const nodeTypes = [
      ...new Set(allNodes.map((node) => node.type).filter(Boolean)),
    ];

    if (nodeTypes.length === 0) {
      setAvailableEntityTypes([]);
      return;
    }

    // Replace (not merge) availableEntityTypes with only types from the graph
    setAvailableEntityTypes(nodeTypes);

    // Update selectedEntityTypes to only include types that are in the graph
    setSelectedEntityTypes((prev) => {
      // Filter to only include types that are actually in the graph
      const validSelected = prev.filter((type) => nodeTypes.includes(type));
      // If no valid selections or all types are new, select all
      if (validSelected.length === 0 || validSelected.length !== prev.length) {
        return nodeTypes;
      }
      return validSelected;
    });
  }, [allNodes]);

  const filteredData = useMemo(() => {
    if (!isInitialized || allNodes.length === 0) {
      return { nodes: [], edges: [] };
    }

    let filteredNodes = allNodes;

    // Apply entity type filter
    if (selectedEntityTypes.length > 0) {
      // Always keep the central node (if it exists) regardless of its type
      const originNodeId =
        centralNodeId ||
        centerEntityId ||
        (initialNodes.length > 0 ? initialNodes[0].id : null);

      filteredNodes = filteredNodes.filter(
        (node) =>
          selectedEntityTypes.includes(node.type) ||
          (originNodeId && node.id === originNodeId)
      );
    }

    // Note: Search term is NOT used to filter the graph
    // It's only used for autocomplete/searching for new entities to add to the graph

    const filteredNodeIds = new Set(filteredNodes.map((node) => node.id));

    // console.log('🔍 Filtered nodes for edges:', {
    //   filteredNodesCount: filteredNodes.length,
    //   filteredNodeIds: Array.from(filteredNodeIds),
    //   allEdgesCount: allEdges.length,
    //   sampleNodes: filteredNodes.slice(0, 3).map(n => ({ id: n.id, name: n.name, type: n.type }))
    // });

    // Process edges to ensure proper structure
    // Create placeholder nodes for missing entities (orphan relationships)
    // BUT: When entity type filtering is active, don't create placeholders - only show edges between filtered nodes
    const placeholderNodes = [];
    const placeholderNodeIds = new Set();
    const isEntityTypeFilterActive = selectedEntityTypes.length > 0;

    const filteredEdges = allEdges.filter((edge) => {
      const sourceId = edge.source?.id || edge.source_entity_id || edge.source;
      const targetId = edge.target?.id || edge.target_entity_id || edge.target;
      const hasSource = filteredNodeIds.has(sourceId);
      const hasTarget = filteredNodeIds.has(targetId);

      // If entity type filtering is active, only include edges where BOTH nodes are in the filtered set
      if (isEntityTypeFilterActive) {
        return hasSource && hasTarget;
      }

      // Otherwise, create placeholders for missing nodes (for orphan relationships)
      // If source is missing, create placeholder
      if (!hasSource && sourceId && !placeholderNodeIds.has(sourceId)) {
        placeholderNodes.push({
          id: sourceId,
          name: `Unknown Entity (${sourceId.substring(0, 8)}...)`,
          type: "Unknown",
          isPlaceholder: true,
        });
        placeholderNodeIds.add(sourceId);
      }

      // If target is missing, create placeholder
      if (!hasTarget && targetId && !placeholderNodeIds.has(targetId)) {
        placeholderNodes.push({
          id: targetId,
          name: `Unknown Entity (${targetId.substring(0, 8)}...)`,
          type: "Unknown",
          isPlaceholder: true,
        });
        placeholderNodeIds.add(targetId);
      }

      // Include edge if at least one node exists (or both)
      return (hasSource || sourceId) && (hasTarget || targetId);
    });

    // Combine filtered nodes with placeholder nodes
    const allAvailableNodes = [...filteredNodes, ...placeholderNodes];
    const allAvailableNodeIds = new Set(allAvailableNodes.map((n) => n.id));

    // Map edges to use actual or placeholder nodes
    const mappedEdges = filteredEdges.map((edge) => {
      const sourceId = edge.source?.id || edge.source_entity_id || edge.source;
      const targetId = edge.target?.id || edge.target_entity_id || edge.target;

      const sourceNode = allAvailableNodes.find((n) => n.id === sourceId);
      const targetNode = allAvailableNodes.find((n) => n.id === targetId);

      return {
        ...edge,
        id: edge.id,
        source: sourceNode || {
          id: sourceId,
          name: `Unknown Entity (${String(sourceId).slice(0, 8)}...)`,
          type: "Unknown",
          isPlaceholder: true,
        },
        target: targetNode || {
          id: targetId,
          name: `Unknown Entity (${String(targetId).slice(0, 8)}...)`,
          type: "Unknown",
          isPlaceholder: true,
        },
        type: edge.type || edge.data?.relationshipType || "Unknown",
        data: {
          relationshipType:
            edge.type || edge.data?.relationshipType || "Unknown",
        },
      };
    });

    // console.log('🔍 Final filtered edges:', mappedEdges.length, mappedEdges);

    // Remove nodes that don't have any edges (except origin node)
    const originNodeId =
      centralNodeId ||
      centerEntityId ||
      (initialNodes.length > 0 ? initialNodes[0].id : null);
    const nodesWithEdges = new Set();

    // Collect all node IDs that have edges
    mappedEdges.forEach((edge) => {
      if (edge.source?.id) nodesWithEdges.add(edge.source.id);
      if (edge.target?.id) nodesWithEdges.add(edge.target.id);
    });

    // Filter out nodes without edges, but always keep the origin node and placeholder nodes
    const finalFilteredNodes = allAvailableNodes.filter(
      (node) =>
        nodesWithEdges.has(node.id) ||
        (originNodeId && node.id === originNodeId) ||
        node.isPlaceholder
    );

    return {
      nodes: finalFilteredNodes,
      edges: mappedEdges,
    };
  }, [
    allNodes,
    allEdges,
    selectedEntityTypes,
    searchTerm,
    centralNodeId,
    centerEntityId,
    initialNodes,
  ]);

  // Show search interface if not suppressing selection and no data to display
  // Check both props (initialNodes) and internal state (allNodes)
  const hasDataFromProps = processedData.processedNodes.length > 0;
  const hasDataFromState = allNodes.length > 0;
  const hasGraphData = hasDataFromProps || hasDataFromState;

  /** Waiting on API for a centered graph with no bootstrap yet */
  const showCenterLoadingPlaceholder =
    !suppressSelection &&
    !hasGraphData &&
    Boolean(centerEntityId && autoLoadCenter);

  /** Truly empty graph (no center id), e.g. standalone route */
  const showStandaloneEmptyHint =
    !suppressSelection && !hasGraphData && !centerEntityId;

  const shellClassName = embedded
    ? embeddedUnifiedChrome && embeddedFillHeight
      ? "relative flex h-full min-h-0 w-full min-h-[220px] flex-1 flex-col overflow-hidden bg-transparent"
      : embeddedFillHeight
        ? "relative flex flex-col w-full flex-1 min-h-0 min-h-[220px] h-full bg-slate-50 overflow-hidden rounded-lg border border-slate-200"
        : "relative flex flex-col w-full min-h-[260px] h-[min(62vh,520px)] max-h-[78vh] bg-slate-50 overflow-hidden shrink-0 rounded-b-lg border border-slate-200"
    : "flex flex-col fixed inset-0 z-50 bg-gray-900";

  if (showCenterLoadingPlaceholder) {
    return (
      <div className={shellClassName}>
        <GraphHeader
          title={title}
          centralNodeId={centralNodeId}
          allNodes={allNodes}
          onClose={onClose}
          tone={uiTone}
          showToolbar={false}
          leading={headerLeading}
          unifiedInCard={embeddedUnifiedChrome}
        />
        <div
          className={
            uiTone === "light"
              ? "flex-1 flex flex-col items-center justify-center gap-3 p-8 text-slate-700"
              : "flex-1 flex flex-col items-center justify-center gap-3 p-8 text-gray-300"
          }
        >
          {error ? (
            <p className="text-sm text-rose-600 text-center max-w-sm">{error}</p>
          ) : (
            <>
              <svg
                className={
                  uiTone === "light"
                    ? "h-8 w-8 animate-spin text-cyan-600"
                    : "h-8 w-8 animate-spin text-blue-400"
                }
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-sm font-medium">Loading…</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (showStandaloneEmptyHint) {
    return (
      <div className={shellClassName}>
        <GraphHeader
          title={title}
          centralNodeId={centralNodeId}
          allNodes={allNodes}
          onClose={onClose}
          tone={uiTone}
          showToolbar={false}
          leading={headerLeading}
          unifiedInCard={embeddedUnifiedChrome}
        />
        <div
          className={
            uiTone === "light"
              ? "flex-1 flex items-center justify-center p-6 text-sm text-slate-600 text-center"
              : "flex-1 flex items-center justify-center p-6 text-sm text-gray-400 text-center"
          }
        >
          <p className="max-w-md">
            Open an entity from Entity Explorer or another screen that passes a center entity to view
            its relationship graph here.
          </p>
        </div>
      </div>
    );
  }

  // Main graph interface
  console.log(`🔍 [${componentId}] SHOWING MAIN GRAPH INTERFACE`);
  return (
    <div className={shellClassName}>
      {isLoadingEntity && (
        <div
          className={
            uiTone === "light"
              ? "absolute inset-0 z-50 flex items-center justify-center bg-slate-50/85 backdrop-blur-sm"
              : "absolute inset-0 z-50 flex items-center justify-center bg-gray-900/70 backdrop-blur-sm"
          }
        >
          <div
            className={
              uiTone === "light"
                ? "bg-white rounded-lg p-6 shadow-lg border border-slate-200"
                : "bg-gray-800 rounded-lg p-6 shadow-xl"
            }
          >
            <svg
              className={
                uiTone === "light"
                  ? "animate-spin w-8 h-8 text-cyan-600 mb-3 mx-auto"
                  : "animate-spin w-8 h-8 text-blue-400 mb-3 mx-auto"
              }
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p
              className={
                uiTone === "light"
                  ? "text-sm text-slate-700 text-center"
                  : "text-sm text-gray-200 text-center"
              }
            >
              Loading entity and connections...
            </p>
          </div>
        </div>
      )}
      <GraphHeader
        title={title}
        centralNodeId={centralNodeId}
        allNodes={allNodes}
        onClose={onClose}
        tone={uiTone}
        leading={headerLeading}
        unifiedInCard={embeddedUnifiedChrome}
        handleResetLayout={handleResetLayout}
        handleFilterToggle={handleFilterToggle}
        handleTipsToggle={handleTipsToggle}
        handleZoomIn={() => handleZoomIn.current?.()}
        handleZoomOut={() => handleZoomOut.current?.()}
        handleFitToView={() => handleFitToView.current?.()}
        showNavTips={showNavTips}
        showEntityTypesPanel={showEntityTypesPanel}
        handleLoadConnections={handleLoadConnections}
        selectedEntity={selectedEntity}
      />

      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <GraphCanvas
          filteredData={filteredData}
          layoutType={layoutType}
          layoutKey={layoutKey}
          typeColors={typeColors}
          selectedEntityTypes={selectedEntityTypes}
          showNavTips={showNavTips}
          showEntityTypesPanel={showEntityTypesPanel}
          handleNodeClick={handleNodeClick}
          handleEdgeClick={handleEdgeClick}
          handleNodeHover={handleNodeHover}
          handleEdgeHover={handleEdgeHover}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
          handleFitToView={handleFitToView}
          handleResetLayoutRef={handleResetLayoutRef}
          onLayoutUpdate={() => setLayoutKey((prev) => prev + 1)}
          handleLoadConnections={handleLoadConnections}
          uiTone={uiTone}
        />

        <EntityTypeFilter
          showEntityTypesPanel={showEntityTypesPanel}
          availableEntityTypes={availableEntityTypes}
          selectedEntityTypes={selectedEntityTypes}
          setSelectedEntityTypes={setSelectedEntityTypes}
          typeColors={typeColors}
          typeLabels={typeLabels}
          tone={uiTone}
          variant="floating"
          onClose={() => setShowEntityTypesPanel(false)}
        />

        <TipsPanel
          showNavTips={showNavTips}
          tone={uiTone}
          variant="floating"
          onClose={() => setShowNavTips(false)}
        />
      </div>

      <EntityDetailsPopup
        selectedEntity={selectedEntity}
        onClose={() => setSelectedEntity(null)}
        onLoadConnections={handleLoadConnections}
      />

      <RelationshipDetailsPopup
        selectedEdge={selectedEdge}
        onClose={() => setSelectedEdge(null)}
      />
    </div>
  );
};

export default GraphVisualization;
